import React, { useState, useEffect, useRef } from 'react';
import { HashRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import CustomCursor from './components/CustomCursor';
import Navbar from './components/Navbar';
import MobileBottomNav from './components/MobileBottomNav';
import Footer from './components/Footer';
import Home from './pages/Home';
import Services from './pages/Services';
import About from './pages/About';
import Projects from './pages/Projects';
import Contact from './pages/Contact';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ErrorPage, { Error404, Error500, Error403 } from './pages/ErrorPage';

// Enhanced PageTransition with a "System Wipe" effect
const PageTransition: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isFirstMount = useRef(true);

  useEffect(() => {
    isFirstMount.current = false;
  }, []);

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div key={location.pathname} className="relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        >
          {children}
        </motion.div>

        {/* Only trigger the Wipe animation on actual route changes, not the first mount of the app */}
        {!isFirstMount.current && (
          <motion.div
            initial={{ scaleY: 0 }}
            animate={{ 
              scaleY: [0, 1, 1, 0],
              originY: ["0%", "0%", "100%", "100%"]
            }}
            transition={{ 
              duration: 1.2, 
              times: [0, 0.4, 0.6, 1],
              ease: "easeInOut" 
            }}
            className="fixed inset-0 bg-background z-[100] flex flex-col items-center justify-center pointer-events-none"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 1, 0] }}
              transition={{ duration: 1.2, times: [0, 0.3, 0.7, 1] }}
              className="flex flex-col items-center"
            >
              <div className="text-2xl font-display font-bold text-white mb-4 tracking-tighter">
                DUOSTACK<span className="text-accent"> SYSTEMS</span>
              </div>
              <div className="w-40 h-[1px] bg-zinc-800 relative overflow-hidden">
                <motion.div 
                  className="absolute inset-0 bg-accent"
                  initial={{ x: "-100%" }}
                  animate={{ x: "100%" }}
                  transition={{ duration: 0.8, ease: "easeInOut" }}
                />
              </div>
            </motion.div>
            <div className="absolute top-0 left-0 w-full h-1 bg-accent/20 blur-sm animate-[scan_1.2s_linear_infinite]" />
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

const Preloader = () => (
  <motion.div
    key="preloader"
    initial={{ opacity: 1 }}
    exit={{ opacity: 0, scale: 1.05 }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    className="fixed inset-0 bg-background flex items-center justify-center z-[9999]"
  >
    <motion.div className="flex flex-col items-center">
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-3xl font-display font-bold text-white mb-6 tracking-tighter"
      >
        DUOSTACK<span className="text-accent"> SYSTEMS</span>
      </motion.div>
      <div className="w-64 h-1 bg-zinc-900 rounded-full overflow-hidden relative border border-white/5">
        <motion.div
          className="h-full bg-accent"
          initial={{ width: "0%" }}
          animate={{ width: "100%" }}
          transition={{ duration: 2.2, ease: "easeInOut" }}
        />
        <motion.div 
          animate={{ x: ["-100%", "200%"] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
          className="absolute inset-0 w-20 bg-gradient-to-r from-transparent via-white/30 to-transparent"
        />
      </div>
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-4 text-[10px] font-mono text-zinc-600 uppercase tracking-[0.3em]"
      >
        Establishing Kernel Connection...
      </motion.div>
    </motion.div>
  </motion.div>
);

const App: React.FC = () => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Artificial delay to ensure preloader dominance
    const timer = setTimeout(() => setLoading(false), 2600);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-background text-zinc-100 selection:bg-accent/30 selection:text-white">
      <CustomCursor />
      
      <AnimatePresence mode="wait">
        {loading ? (
          <Preloader key="boot-sequence" />
        ) : (
          <motion.div 
            key="application-root"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <Router>
              <Navbar />
              <MobileBottomNav />
              <main className="pb-24 md:pb-0">
                <PageTransition>
                  <Routes>
                    {/* Main Routes */}
                    <Route path="/" element={<Home />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/projects" element={<Projects />} />
                    <Route path="/contact" element={<Contact />} />
                    
                    {/* Legal Pages (Linked from Footer) */}
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/terms-of-service" element={<TermsOfService />} />
                    
                    {/* Error Pages */}
                    <Route path="/error" element={<ErrorPage />} />
                    <Route path="/error/404" element={<Error404 />} />
                    <Route path="/error/500" element={<Error500 />} />
                    <Route path="/error/403" element={<Error403 />} />
                    
                    {/* Catch-all 404 Route - Must be last */}
                    <Route path="*" element={<Error404 />} />
                  </Routes>
                </PageTransition>
              </main>
              <Footer />
            </Router>
          </motion.div>
        )}
      </AnimatePresence>
      
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes scan {
          0% { top: 0%; opacity: 0; }
          50% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
      `}} />
    </div>
  );
};

export default App;